"use client";
import Link from "next/link";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { ArrowRight, Search } from "lucide-react";
import { usePathname } from "next/navigation";
import { useBannerContext } from "@core/helpers/client/contexts/BannerContext";
import { useMenusContext } from "@core/helpers/client/contexts/MenusContext";
import { useAuthContext } from "@core/helpers/client/contexts/AuthContext";
import AuthButtonModal from "@/components/pages/Auth/_ButtonModal";
import BannerSection, { should_show_banner } from "./_banner";
import { useInitialBanners } from "@/contexts/InitialBanners";
import useScrollDirection from "./useScrollDirection";
import LoadingSpinner from "@/components/LoadingSpinner";
import MenuCategoryHeader from "./_navBlog";
import { BLOG_CATEGORIES_CHANGE_EVENT, BLOG_CATEGORIES_TOGGLE_EVENT } from "../FooterNavigationBlog";
import useSafeBack from "@core/helpers/client/hooks/useSafeBack";
import useIsDesktop from "@core/helpers/client/hooks/useIsDesktop";
import { useImageDevice } from "@core/helpers/client/contexts/ImagePriorityContext";
import useResizeMeasure from "@/hooks/useResizeMeasure";


const NAV_TRANSITION = 500;

const NavLoadingBlog = () => (
    <nav className="hidden w-full lg:flex items-center justify-center gap-6 border-b border-jmt-neutral-200">
        {[...Array(5)].map((_, index) => (
            <div key={index} className="flex items-center py-4">
                <div className="h-5.5 w-[90px] xl:w-[110px] rounded-md bg-jmt-neutral-100" />
            </div>
        ))}
    </nav>
);

export default function HeaderBlog({ banner_count = 0 }) {

    const { banner_data, banner_loading } = useBannerContext();
    const show_banner = should_show_banner({ banner_data, banner_loading, initial_count: banner_count, initial_banners: useInitialBanners() });
    const { menus, menus_loading } = useMenusContext();
    // دسته‌های مجله از سرور می‌آیند (root layout)، پس منتظر بقیه‌ی منوها نمی‌مانیم
    const categories_ready = menus?.["category-list"] !== undefined || !menus_loading;
    const { user_loading } = useAuthContext();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const pathname = usePathname() || "";

    // SSR و hydration فقط هدر دستگاهِ User-Agent را دارند (تبلت و ناشناس هر دو را، مثل DeviceOnly)؛
    // بعد از hydration عرض واقعی صفحه تصمیم می‌گیرد. هر هدر یک نسخه‌ی کامل فهرست دسته‌ها را دارد.
    const is_desktop = useIsDesktop();
    const server_device = useImageDevice();
    const show_mobile = is_desktop === null ? server_device !== "desktop" : !is_desktop;
    const show_desktop = is_desktop === null ? server_device !== "mobile" : is_desktop;
    const isBlogHome = pathname.replace(/\/+$/, "") === "/blogs";

    // ورود مستقیم به صفحه (مثلا از گوگل) نباید با «بازگشت» کاربر را از سایت بیرون ببرد
    const handleBack = useSafeBack();

    useEffect(() => {
        // با تغییر مسیر، منوی دسته‌بندی بسته شود
        setMobileMenuOpen(false);
    }, [pathname]);
    const showNavbar = useScrollDirection();
    const [navExpanded, setNavExpanded] = useState(true);

    useEffect(() => {
        if (!showNavbar) {
            setNavExpanded(false);
            return;
        }

        const timeout = setTimeout(() => setNavExpanded(true), NAV_TRANSITION);
        return () => clearTimeout(timeout);
    }, [showNavbar]);

    const mobileHeaderRef = useRef(null);
    const desktopHeaderRef = useRef(null);
    const navWrapRef = useRef(null);

    // فقط در ResizeObserver اندازه گرفته می‌شود (useResizeMeasure)؛ تغییر اندازه‌ی پنجره هم ارتفاع هدر را عوض می‌کند و
    // همین observer آن را می‌گیرد. اول هر سه اندازه خوانده و بعد هر دو متغیر نوشته می‌شوند.
    useResizeMeasure(
        () => [mobileHeaderRef.current, desktopHeaderRef.current, navWrapRef.current],
        () => {
            const mobile = mobileHeaderRef.current?.offsetHeight || 0;
            const desktop = desktopHeaderRef.current?.offsetHeight || 0;
            const nav_height = navWrapRef.current?.clientHeight || 0;

            const height = Math.max(mobile, desktop);
            // ارتفاع هدر وقتی ردیف دوم بسته است؛ فهرست مطالب مقصد اسکرول را با این می‌سنجد
            const collapsed = Math.max(mobile, desktop - nav_height);

            return () => {
                const root_style = document.documentElement.style;
                root_style.setProperty("--header-height", `${height}px`);
                root_style.setProperty("--header-height-collapsed", `${collapsed}px`);
            };
        },
        [banner_data, showNavbar, is_desktop]
    );

    useEffect(
        () => () => document.documentElement.style.removeProperty("--header-height-collapsed"),
        []
    );

    // دکمه‌ی «دسته‌بندی» نویگیشن فوتر مجله همین منوی کشویی را باز/بسته می‌کند
    useEffect(() => {
        const toggle = () => setMobileMenuOpen((prev) => !prev);
        window.addEventListener(BLOG_CATEGORIES_TOGGLE_EVENT, toggle);
        return () => window.removeEventListener(BLOG_CATEGORIES_TOGGLE_EVENT, toggle);
    }, []);

    useEffect(() => {
        window.dispatchEvent(new CustomEvent(BLOG_CATEGORIES_CHANGE_EVENT, { detail: { open: mobileMenuOpen } }));
    }, [mobileMenuOpen]);

    return (
        <>

            {/* mobile */}
            {show_mobile && <header dir="rtl" className="relative block lg:hidden w-full bg-jmt-white">
                <div ref={mobileHeaderRef} className="fixed top-0 right-0 left-0 z-40 bg-jmt-white">

                    {show_banner && <BannerSection initial_count={banner_count} />}

                    <section className="w-full bg-jmt-white shadow-buttom-light">
                        <div className="container">
                            {/* منو، جستجو و ورود به نویگیشن فوتر مجله منتقل شده‌اند؛ اینجا فقط برگشت و لوگو */}
                            <div className="w-full py-3 grid grid-cols-[40px_1fr_40px] items-center gap-4">

                                {isBlogHome ? <span /> : (
                                    <button
                                        type="button"
                                        onClick={handleBack}
                                        aria-label="بازگشت"
                                        className="w-10 h-10 flex items-center justify-center text-jmt-neutral-900">
                                        <ArrowRight size={24} />
                                    </button>
                                )}

                                <Link href="/blogs/" className="flex justify-self-center">
                                    <div className="w-[130px] h-6">
                                        <Image
                                            src="/images/logo/blog-logo.png"
                                            unoptimized
                                            alt="لوگو ژومنت"
                                            className="object-contain w-full h-full"
                                            width={130}
                                            height={24}
                                        />
                                    </div>
                                </Link>

                                {/* ستون خالی برای وسط‌ماندن لوگو */}
                                <span />

                            </div>
                        </div>
                    </section>

                </div>

                {categories_ready && (<MenuCategoryHeader mobileOpen={mobileMenuOpen} setMobileOpen={setMobileMenuOpen} />)}
            </header>}

            {/* desktop */}
            {show_desktop && <header dir="rtl" className="relative hidden lg:block w-full bg-jmt-white">
                <div ref={desktopHeaderRef} className="fixed top-0 right-0 left-0 z-40 bg-jmt-white">

                    {show_banner && <BannerSection initial_count={banner_count} />}

                    <section className="w-full">

                        {/* row 1 */}
                        <div className="w-full mx-auto py-4 border-b border-jmt-neutral-100">
                            <div className="container">
                                <div className="w-full flex items-center justify-between">
                                    <Link href="/" className="text-sm font-bold text-jmt-primary-800 hover:text-jmt-primary-900"> رفتن به فروشگاه ژومنت </Link>

                                    <Link href="/blogs" className="flex">
                                        <div className="w-[194px] h-10">
                                            <Image
                                                src="/images/logo/blog-logo.png"
                                                unoptimized
                                                alt="لوگو ژومنت"
                                                className="object-contain w-full h-full"
                                                width={194}
                                                height={40}
                                            />
                                        </div>
                                    </Link>

                                    <div className="flex items-center justify-end gap-6">
                                        <Link href="/blogs/search" aria-label="جستجو در مجله" className="w-10 h-10 flex items-center justify-center">
                                            <Search size={24} className="text-jmt-neutral-800" />
                                        </Link>
                                        {user_loading ? <LoadingSpinner /> : <AuthButtonModal />}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* row 2 */}
                        {!categories_ready ? <NavLoadingBlog />
                            : <div ref={navWrapRef} className={`transition-all duration-500 ease-in-out border-b border-jmt-neutral-100 ${showNavbar ? "max-h-20 opacity-100" : "max-h-0 opacity-0"} ${navExpanded ? "overflow-visible" : "overflow-hidden"}`}>
                                <MenuCategoryHeader mobileOpen={mobileMenuOpen} setMobileOpen={setMobileMenuOpen} />
                            </div>}
                    </section>
                </div>
            </header>}
        </>
    );
}